import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Layers, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs, readyPills } from "@/components/layout/Crumbs";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { PickShopGate } from "@/components/mall/PickShopGate";
import { SellerListings } from "@/components/SellerListings";
import { cardTheme, receded, sortStoreFirst, themeOf } from "@/lib/mall";
import { PhotoCard, garmentCard } from "@/components/PhotoCard";
import { DTF_PAGE, READY_BY_KEY, READY_IN } from "@/lib/nav";
import { StoreButton } from "@/components/Buttons";
import { PageBanner } from "@/components/mall/PageBanner";

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** One pill row of filters: a mono label, then the choices. */
function FilterRow({ label, choices, value, onPick, theme, testid }) {
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
            <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
            {choices.map((c) => {
                const on = (c.key || "all") === (value || "all");
                return (
                    <button key={c.key || "all"} type="button" onClick={() => onPick(c.key)} data-testid={`${testid}-${c.key || "all"}`} aria-pressed={on}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "border-transparent" : "border-zinc-200 text-zinc-700 hover:border-zinc-400"} ${c.count === 0 ? "opacity-40" : ""}`}
                        style={on ? { background: theme.primary, color: theme.on || "#fff" } : undefined}>
                        {c.hex && <span className="h-3 w-3 rounded-full border border-black/15" style={{ background: c.hex }} />}
                        {c.label}
                    </button>
                );
            })}
        </div>
    );
}

/** The DTF counter as its own strip on the shelf — the one product under Shirts that is not a garment. */
function DtfStrip({ dtf, theme, storeSlug }) {
    const cfg = dtf.config || {};
    const q = storeSlug ? `?store=${storeSlug}` : "";
    return (
        <section className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 bg-white" data-testid="dtf-section">
            <div className="grid md:grid-cols-[220px_1fr]">
                <Link to={`${DTF_PAGE.to}${q}`} className="relative block aspect-[4/3] bg-zinc-100 md:aspect-auto" data-testid="dtf-strip-image">
                    {dtf.kind?.image_url && <img src={mediaUrl(dtf.kind.image_url)} alt="DTF gang sheet" className="absolute inset-0 h-full w-full object-cover" />}
                </Link>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 p-5">
                    <div className="min-w-0 flex-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Transfers · {dtf.ready_label}</p>
                        <h2 className="mt-1 font-display text-2xl font-black tracking-tight text-zinc-900" data-testid="dtf-strip-title">DTF Gang Sheet</h2>
                        <p className="mt-1 text-sm text-zinc-600">
                            {cfg.width_inches || 16.5}" wide, any length. Upload every design you have, we nest them {cfg.min_spacing_inches || 0.5}" apart, you press them on anything.
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="font-mono text-2xl font-black leading-none" style={{ color: theme.ink || theme.primary }} data-testid="dtf-strip-price">
                            ${Number(cfg.price_per_linear_foot || 5).toFixed(2)}
                        </p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">per linear foot</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to={`/shirts/dtf/gang-sheet${q}`} data-testid="dtf-strip-build">
                            <StoreButton theme={theme} className="px-4 py-2.5 text-xs"><Layers size={14} /> Build a sheet</StoreButton>
                        </Link>
                        <Link to={`${DTF_PAGE.to}${q}`} data-testid="dtf-strip-more"
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-4 py-2.5 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                            How it works <ArrowRight size={13} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/** The Shirts shelf: every garment is one card per color, filtered by Ready-in, color and style. */
export default function Shirts() {
    const [params, setParams] = useSearchParams();
    const { slug: activeSlug, store: activeStore } = useMall();
    const { shopSlug } = useShop();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const ready = READY_BY_KEY[params.get("ready")] ? params.get("ready") : null;
    const color = params.get("color") || null;
    const style = params.get("style") || null;

    useEffect(() => {
        if (!shopSlug) return;
        setLoading(true);
        api.get("/catalog/shirts", { params: { store: shopSlug, ready: ready || undefined } })
            .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, [shopSlug, ready]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next);
    };

    if (!shopSlug) return <Layout><PickShopGate room="shirts" title="Which shop prints your shirts?" to="/shirts" /></Layout>;
    if (loading && !data) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;
    if (!data) return <Layout><div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-3xl font-black">The shelf is empty</h1></div></Layout>;

    const theme = themeOf(activeStore);
    const readyItem = ready ? READY_BY_KEY[ready] : null;
    const all = sortStoreFirst(data.products, activeSlug);
    const groups = [{ key: null, label: "All" }, ...Object.entries(data.groups || {}).map(([key, label]) => ({ key, label })),
        { key: "embroidery", label: "Embroidery" }, { key: "screenprint", label: "Screen print" }];
    const groupOf = (p) => p.style_group || p.category;
    const colors = [{ key: null, label: "All" }, ...(data.colors || []).map((c) => ({ key: slug(c.name), label: c.name, hex: c.hex }))];
    const shown = all.filter((p) => (!color || slug(p.color_name) === color) && (!style || groupOf(p) === style));
    const countBy = (fn) => (k) => (k ? all.filter((p) => fn(p) === k).length : all.length);

    return (
        <Layout>
            <PageBanner room={ready === "same_day" ? "white" : "apparel"} />
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="shirts-page">
                <Reveal>
                    <Crumbs trail={[{ label: "Shirts", to: "/shirts" }, ...(readyItem ? [{ label: readyItem.label }] : [])]}
                        pills={readyPills("/shirts", data.counts, ready, READY_IN)} />
                    <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="shirts-title">
                            Shirts{readyItem ? <span className="text-zinc-400"> · {readyItem.label}</span> : ""}
                        </h1>
                        <p className="text-sm text-zinc-500" data-testid="shirts-note">
                            {shown.length + (data.dtf?.show ? 1 : 0)} option{shown.length + (data.dtf?.show ? 1 : 0) === 1 ? "" : "s"}
                            {activeStore ? <> · printed by <span className="font-semibold text-zinc-900">{activeStore.name}</span></> : null}
                            {readyItem ? ` · ${readyItem.note}` : " · every style in six colors, each with its own ready-in"}
                        </p>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                        <FilterRow label="Color" theme={theme} value={color} onPick={(v) => setFilter("color", v)} testid="filter-color"
                            choices={colors.map((c) => ({ ...c, count: countBy((p) => slug(p.color_name))(c.key) }))} />
                        <FilterRow label="Style" theme={theme} value={style} onPick={(v) => setFilter("style", v)} testid="filter-style"
                            choices={groups.map((g) => ({ ...g, count: countBy(groupOf)(g.key) }))} />
                    </div>
                </Reveal>

                {data.dtf?.show && !color && !style && <DtfStrip dtf={data.dtf} theme={theme} storeSlug={shopSlug} />}

                <div className="mt-6 flex items-baseline justify-between">
                    <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500" data-testid="shirts-grid-heading">
                        Shirts · {shown.length}
                    </h2>
                    {(color || style) && (
                        <button type="button" onClick={() => setParams(ready ? { ready } : {})} className="text-xs font-semibold underline" data-testid="filter-clear">Clear filters</button>
                    )}
                </div>
                {shown.length ? (
                    <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="shirts-grid">
                        {shown.map((p, i) => (
                            <Reveal key={p.slug} delay={Math.min(i, 8) * 0.04}>
                                <PhotoCard {...garmentCard(p, { name: p.group_label || p.kind_name }, activeStore, activeSlug, cardTheme)} receded={receded(p, activeSlug)} />
                            </Reveal>
                        ))}
                    </div>
                ) : (
                    <div className="mt-3 rounded-2xl border border-dashed border-zinc-200 bg-white p-10 text-center" data-testid="shirts-empty">
                        <p className="text-base font-semibold text-zinc-900">Nothing here with those filters.</p>
                        <Link to="/shirts" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-zinc-900 underline">See every shirt <ArrowRight size={14} /></Link>
                    </div>
                )}
            </section>

            <SellerListings kind="apparel" group="white" heading="Also for sale from other sellers"
                blurb="Classified ads for blanks and finished apparel, posted by other shops. You deal with the seller directly."
                sellCta="Sell your apparel" testid="seller-apparel-shirts" />
        </Layout>
    );
}
